import { Accordion, Badge, Card, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useQuery } from "react-query";
import { DEV_SERVER } from "../../../constants/urls";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import axios from "axios";
import { userSignal } from "../../../signal/userSignal";
import * as moment from "moment";
import DataTable from "react-data-table-component";
import avatar1 from "../../../assets/images/avatars/01.png";
import { allUsersSignal } from "../../../signal/allUsersSignal";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";

const STAGE_COLORS = {
    Pending: "warning",
    Approved: "success",
    Rejected: "danger",
};

export default function SuggestionList() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [suggestions, setSuggestions] = useState([]);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(35);
    const [totalItems, setTotalItems] = useState(0);
    const [userId, setUserId] = useState(searchParams.get("userId") || "");
    const [farmerId] = useState(searchParams.get("farmerId") || "");
    const [tripId] = useState(searchParams.get("tripId") || "");
    const [fromDate, setFromDate] = useState(searchParams.get("fromDate") || "");
    const [toDate, setToDate] = useState(searchParams.get("toDate") || "");

    const navigate = useNavigate();

    const getSuggestions = useQuery(
        ["Suggestions", page, perPage, userId, farmerId, tripId, fromDate, toDate],
        async ({ signal }) => {
            const params = new URLSearchParams({
                page,
                limit: perPage,
                ...(userId && { userId }),
                ...(farmerId && { farmerId }),
                ...(tripId && { tripId }),
                ...(fromDate && { startTime: new Date(fromDate).setHours(0, 0, 0, 0) }),
                ...(toDate && { endTime: new Date(toDate).setHours(23, 59, 59, 999) }),
            });
            const resp = await axios.get(`${DEV_SERVER}/suggestion/v2/?${params.toString()}`, {
                headers: { Authorization: `Bearer ${userSignal.value?.token}` },
                signal,
            });
            return resp.data;
        },
        {
            enabled: false,
            onSuccess: (data) => {
                if (data.status) {
                    setSuggestions(data.data || []);
                    setTotalItems(data.data?.length || 0);
                } else {
                    toast.error(data.message);
                }
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || "Something went wrong");
            },
        }
    );

    const refetch = () => getSuggestions.refetch({ cancelRefetch: true });

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage]);

    const handleApplyFilters = () => {
        const params = new URLSearchParams({
            ...(userId && { userId }),
            ...(fromDate && { fromDate }),
            ...(toDate && { toDate }),
        });
        setSearchParams(params);
        refetch();
    };

    const handleReset = () => {
        setUserId("");
        setFromDate("");
        setToDate("");
        setSearchParams({});
        setTimeout(() => refetch(), 0);
    };

    const columns = [
        {
            name: "Farmer",
            center: true,
            selector: (row) => row?.farmer?.name || "—",
        },
        {
            name: "Added By",
            center: true,
            selector: (row) => {
                const user = allUsersSignal.value[row?.userId];
                if (user) {
                    return (
                        <span style={{ display: "flex", columnGap: "0.5rem", alignItems: "center" }}>
                            <img
                                style={{ width: "20px", height: "20px", borderRadius: "50%" }}
                                src={user.profileURL || avatar1}
                                alt=""
                            />
                            {user.name}
                        </span>
                    );
                }
                return "—";
            },
        },
        {
            name: "Stage",
            center: true,
            selector: (row) => (
                <Badge bg={STAGE_COLORS[row?.currentStage] || "secondary"}>
                    {row?.currentStage || "—"}
                </Badge>
            ),
        },
        {
            name: "Items",
            center: true,
            selector: (row) => row?.suggestionItems?.length || 0,
        },
        {
            name: "Total Amount",
            center: true,
            selector: (row) => `₹ ${parseFloat(row?.totalAmount || 0).toFixed(2)}`,
            sortable: true,
        },
        {
            name: "Created At",
            center: true,
            sortable: true,
            selector: (row) => moment(row?.createdAt).format("DD/MM/YY hh:mm A"),
        },
        {
            name: "Action",
            center: true,
            selector: (row) => (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip style={{ position: "fixed" }}>View Details</Tooltip>}
                >
                    <span
                        style={{ cursor: "pointer", color: "#0d6efd" }}
                        onClick={() => navigate(`/dashboard/suggestions/${row?.id || row?._id}`)}
                    >
                        <i className="far fa-eye table-icons"></i>
                    </span>
                </OverlayTrigger>
            ),
        },
    ];

    const isLoading = getSuggestions.isLoading || getSuggestions.isFetching;

    return (
        <Row>
            <Col sm="12">
                <Card>
                    <Card.Header className="d-flex justify-content-between">
                        <div className="header-title">
                            <h4 className="card-title">Suggestion List</h4>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Accordion className="mb-3">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Filters</Accordion.Header>
                                <Accordion.Body>
                                    <div
                                        className="flex-wrap"
                                        style={{
                                            display: "flex",
                                            alignItems: "flex-end",
                                            columnGap: "1rem",
                                            rowGap: "0.7rem",
                                            marginBottom: "1rem",
                                        }}
                                    >
                                        <div>
                                            <label>User</label>
                                            <Select
                                                options={Object.keys(allUsersSignal.value).map((key) => ({
                                                    value: allUsersSignal.value[key].id,
                                                    label: allUsersSignal.value[key].name,
                                                }))}
                                                onChange={(e) => setUserId(e ? e.value : "")}
                                                value={
                                                    allUsersSignal.value[userId]
                                                        ? {
                                                              value: allUsersSignal.value[userId].id,
                                                              label: allUsersSignal.value[userId].name,
                                                          }
                                                        : null
                                                }
                                                isClearable
                                                placeholder="All Users"
                                            />
                                        </div>
                                        <div>
                                            <label>From Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={fromDate}
                                                onChange={(e) => setFromDate(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label>To Date</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={toDate}
                                                onChange={(e) => setToDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-end" style={{ columnGap: "1rem" }}>
                                        <button className="btn btn-light" onClick={handleReset}>
                                            Reset
                                        </button>
                                        <button className="btn btn-primary" onClick={handleApplyFilters}>
                                            Apply Filters
                                        </button>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        {!isLoading && (
                            <DataTable
                                columns={columns}
                                data={suggestions}
                                pagination
                                paginationServer
                                paginationTotalRows={totalItems}
                                paginationPerPage={perPage}
                                paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 35]}
                                paginationDefaultPage={page}
                                onChangeRowsPerPage={(currentRowsPerPage) => setPerPage(currentRowsPerPage)}
                                onChangePage={(currentPage) => setPage(currentPage)}
                                onRowClicked={(row) =>
                                    navigate(`/dashboard/suggestions/${row?.id || row?._id}`)
                                }
                                customStyles={{
                                    rows: {
                                        style: { cursor: "pointer", backgroundColor: "unset" },
                                    },
                                }}
                                noDataComponent={
                                    <div className="p-3 text-muted">No suggestions found.</div>
                                }
                            />
                        )}
                        {isLoading && (
                            <div className="d-flex justify-content-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
}
